import { h } from 'preact';
import { Component, createRef } from 'react';
import styles from './focusTrap.scss';

/**
 * Focus Trap React component
 * Used to trap focus inside the chat widget, while the widget is open
 */
class FocusTrap extends Component {
  trapRef = createRef<HTMLDivElement>();

  firstFocusable: HTMLElement | null = null;

  lastFocusable: HTMLElement | null = null;

  componentDidMount() {
    this.setFocusTrap();
    // listen to key presses
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    // remove key press listener
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  setFocusTrap = () => {
    setTimeout(() => {
      const dialog = this.trapRef.current;
      if (!dialog) return;
      // get all focusable elements inside the dialog
      const focusableElements = [
        'button',
        'input',
        'select',
        'textarea',
        'iframe',
        'object',
        'embed',
        '[href]',
        '[contenteditable]',
        '[tabindex]:not([tabindex="-1"])',
      ];
      const focusableContent = dialog.querySelectorAll(
        focusableElements.join(', '),
      );
      if (focusableContent.length === 0) return;
      this.firstFocusable = focusableContent[0] as HTMLElement | null;
      this.lastFocusable = focusableContent[
        focusableContent.length - 1
      ] as HTMLElement | null;
      this.firstFocusable?.focus();
    }, 0);
  };

  handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      // esca key should close chat widget
      window.dispatchEvent(new Event('kustomerClose'));
    } else if (e.key === 'Tab') {
      if (e.shiftKey && document.activeElement === this.firstFocusable) {
        e.preventDefault();
        // if tabbing backwards and the first focusable element is
        // focused, focus the last focusable element
        this.lastFocusable?.focus();
      } else if (!e.shiftKey && document.activeElement === this.lastFocusable) {
        e.preventDefault();
        // if tabbing forwards and the last focusable element is
        // focused, focus the first focusable element
        this.firstFocusable?.focus();
      }
    }
  };

  render() {
    return (
      <div
        ref={this.trapRef}
        role="dialog"
        aria-modal="true"
        class={styles.focusTrap}
      >
        {this.props.children}
      </div>
    );
  }
}

export default FocusTrap;
